// cart.thunks.ts
import { createAsyncThunk } from '@reduxjs/toolkit';

import { ApiError } from '../../models/common.model';
import type { Cart } from '../../shopify/models/cart.model';
import type { Checkout } from '../../shopify/models/checkout.model';
import type { LineItem } from '../../shopify/models/line-item.model';
import type { Product } from '../../shopify/models/product.model';
import { shopifyService } from '../../shopify/shopify.service';
import { dispatchCartEvent, parseShopifyCartError } from '../utils/cart.utils';
import type { RootState } from '../../store';
import { toastService } from '../../toast/toast.service';
import { getWindow, windowNavigate } from '../../utils/window.utils';
import type { AddToCartRequest, BuyItNowRequest } from '../models/cart.model';
import { CartAction } from '../models/cart.model';

export const ADD_TO_CART_LOADING_CLASS = 'single-add-to-cart-loading';
const cartService = shopifyService.cartService;

// Detect available cart sections for current theme
function detectCartSections(): string[] {
  const sections: string[] = [];

  // Dawn theme - look for cart-drawer or cart-notification
  const cartDrawer = document.querySelector('cart-drawer');
  const cartNotification = document.querySelector('cart-notification');

  if (cartDrawer) {
    // Extract the actual section ID from the shopify-section parent
    const sectionElement = cartDrawer.closest('.shopify-section');
    const sectionId = sectionElement?.id?.replace('shopify-section-', '');
    if (sectionId) {
      sections.push(sectionId);
    }
    sections.push('cart-drawer');
  } else if (cartNotification) {
    // Cart notification needs specific sections
    sections.push('cart-notification-product', 'cart-notification-button');
  }

  // Horizon theme - look for cart-items-component with data-section-id
  const cartItemsComponents = document.querySelectorAll('cart-items-component');
  cartItemsComponents.forEach((component) => {
    const sectionId = component.getAttribute('data-section-id');
    if (sectionId && !sections.includes(sectionId)) {
      sections.push(sectionId);
    }
  });

  // Also check for cart-icon in Horizon
  const cartIcon = document.querySelector('cart-icon');
  if (cartIcon) {
    const sectionId = cartIcon.getAttribute('data-section-id');
    if (sectionId && !sections.includes(sectionId)) {
      sections.push(sectionId);
    }
  }
  
  sections.push('cart-icon-bubble', 'header-cart-count', 'variant-added');

  return sections;
}

// Update cart drawer/notification UI with rendered sections
function updateCartUI(response: any, cart: any): void {
  if (!!response.sections) {
    const cartDrawer = document.querySelector('cart-drawer') as any;
    const cartNotification = document.querySelector('cart-notification') as any;

    if (cartDrawer && response.sections['cart-drawer']) {
      try {
        // Set active element (focus management) - required by cart-drawer
        cartDrawer.setActiveElement(document.activeElement);
        cartDrawer.renderContents(response);

        // FIX: Remove .is-empty class from cart-drawer element itself
        // Shopify's theme only removes it from .drawer__inner, not from <cart-drawer>
        // Without this, CSS rule .is-empty .cart__contents { display: none; } hides the form
        cartDrawer.classList.remove('is-empty');
      } catch (err) {
        console.error('[Single - Cart] Error updating cart drawer:', err);
      }
    } else if (cartNotification && response.sections['cart-notification-product']) {
      try {
        // CartNotification needs a .key property - use the last added item's key
        // This is needed because renderContents looks for an element with id="cart-notification-product-{key}"
        if (cart.items && cart.items.length > 0) {
          response.key = cart.items[cart.items.length - 1].key;
        }

        cartNotification.renderContents(response);

        // Same fix: remove .is-empty class from cart-notification element
        cartNotification.classList.remove('is-empty');
      } catch (err) {
        console.error('[Single - Cart] Error updating cart notification:', err);
      }
    }
  }

  // Horizon
  document.dispatchEvent(
    new CustomEvent('cart:update', {
      bubbles: true,
      detail: {
        resource: cart,
        data: {
          source: 'single-storefront',
        },
      },
    })
  );

  // Impact
  document.documentElement.dispatchEvent(
    new CustomEvent('cart:change', {
      bubbles: true,
      detail: {
        baseEvent: 'variant:add',
        cart: {
          ...cart,
          sections: response.sections,
        },
      },
    })
  );
}

// Add to Cart Thunk
export const addToCart = createAsyncThunk<
  { lineItems: LineItem[]; request: AddToCartRequest },
  AddToCartRequest,
  { state: RootState; rejectValue: ApiError }
>('cart/addToCart', async (request, { getState, rejectWithValue, dispatch }) => {
  const state = getState();
  const memberships = state.membership.memberships.value;
  const product = state.element.product.value as Product;

  // Derive selling plan ID if missing (handles DOM recreation on variant change)
  // All related properties will be auto-derived by lineItemService.processSellingPlanProperties
  if (request.membership && !request.sellingPlanId && product) {
    const variant = product.variants.find(v => v.id.toString() === request.productVariantId);
    if (variant?.selling_plan_allocations?.length) {
      request.sellingPlanId = String(variant.selling_plan_allocations[0].selling_plan_id);
    }
  }

  // Handle button feedback
  if (request.originalAddToCartButton) {
    const button = request.originalAddToCartButton as HTMLButtonElement;
    button.disabled = true;
    button.classList.add(ADD_TO_CART_LOADING_CLASS);
    button.dataset.singleOriginalHtml = button.innerHTML;
    button.innerHTML = '<span>Adding...</span>';
  }

  try {
    const sections = detectCartSections();
    const response = await cartService.addToCart(request, memberships, product, sections, window.location.pathname);
    const cart = await shopifyService.cartService.getCart();

    // Update cart UI (handles both Dawn and Impact/Horizon themes)
    if (response.sections) {
      updateCartUI(response, cart);
    }

    await dispatchCartEvent(cart, 'single-storefront', response.sections);

    // Handle cart navigation
    if (request.cartAction !== CartAction.NONE) {
      const cartUrl = getWindow().routes?.cart_url || '/cart';

      if (request.cartAction === CartAction.OPEN_CART_IN_NEW_TAB) {
        windowNavigate(cartUrl, true);
      } else if (request.cartAction === CartAction.OPEN_CART_IN_CURRENT_TAB) {
        windowNavigate(cartUrl);
      }
    }

    return { lineItems: response as LineItem[], request };
  } catch (error) {
    toastService.error('Error', parseShopifyCartError(error));
    return rejectWithValue(ApiError.Other);
  } finally {
    // Re-enable button
    if (request.originalAddToCartButton) {
      const button = request.originalAddToCartButton as HTMLButtonElement;
      button.disabled = false;
      button.classList.remove(ADD_TO_CART_LOADING_CLASS);
      if (button.dataset.singleOriginalHtml !== undefined) {
        button.innerHTML = button.dataset.singleOriginalHtml;
        delete button.dataset.singleOriginalHtml;
      }
    }
  }
});

// Buy It Now Thunk
export const buyItNow = createAsyncThunk<
  Checkout,
  BuyItNowRequest,
  { state: RootState; rejectValue: ApiError }
>('cart/buyItNow', async (request, { getState, rejectWithValue }) => {
  const state = getState();
  const memberships = state.membership.memberships;
  const product = state.product.product.value;

  try {
    const checkout = await cartService.buyItNow(request, memberships, product);
    windowNavigate(checkout.web_url);
    return checkout;
  } catch (error) {
    toastService.error('Error', 'Unable to add to cart.');
    return rejectWithValue(ApiError.Other);
  }
});

// Load Cart Thunk
export const loadCart = createAsyncThunk<Cart, void, { rejectValue: ApiError }>(
  'cart/loadCart',
  async (_, { rejectWithValue }) => {
    try {
      const cart = await cartService.getCart();

      if (!cart) {
        return rejectWithValue(ApiError.Other);
      }

      return cart;
    } catch (error) {
      return rejectWithValue(ApiError.Other);
    }
  }
);
